import React from 'react';
import { ArrowRight, Truck, Wrench, ShoppingBag, Briefcase, CheckCircle, Clock, MapPin, Shield } from 'lucide-react';
import SEO from '../components/SEO';

const BusinessStorage = () => {
    const businessTypes = [
        {
            icon: <Truck className="h-10 w-10 text-green-600" />,
            title: 'Landscapers & Lawn Care',
            description: 'Store mowers, trimmers, and trailers securely. Avoid HOA fines for parking at home.'
        },
        {
            icon: <Wrench className="h-10 w-10 text-green-600" />,
            title: 'Plumbers, Electricians & HVAC',
            description: 'Keep parts, pipes, and tools organized. A 10x20 unit works like a mini-warehouse.'
        },
        {
            icon: <ShoppingBag className="h-10 w-10 text-green-600" />,
            title: 'E-Commerce & Retail',
            description: 'Keep inventory clean and organized. Drive-up access makes shipping days easy.'
        },
        {
            icon: <Briefcase className="h-10 w-10 text-green-600" />,
            title: 'Pharmaceutical & Sales Reps',
            description: 'Store samples and marketing materials centrally. Grab what you need and hit the road.'
        }
    ];

    const benefits = [
        'Zero Wasted Time: No elevators, no stairs, no long walks. Park at your door and go.',
        'Extended Access Hours: Gate access from 6:00 AM to 10:00 PM, 7 days a week.',
        'Prime Location: Right on S. Stemmons Fwy (I-35E) service road.'
    ];

    const sizeRecommendations = [
        {
            size: '10\' x 10\'',
            title: 'Document & Inventory',
            description: 'Holds roughly 200 file boxes or shelving units for retail stock.'
        },
        {
            size: '10\' x 15\'',
            title: 'The "Work Van" Alternative',
            description: 'Fits ladders, tool chests, and mid-sized equipment. (Comparable to Large Master Bedroom)'
        },
        {
            size: '10\' x 20\'',
            title: 'Mini-Warehouse',
            description: 'Comparable to a single-car garage. Fits large machinery or bulk materials.'
        },
        {
            size: '10\' x 30\'',
            title: 'Commercial Hub',
            description: 'Extra Large Garage size. Fits 4-5 bedroom house contents or extensive commercial inventory.'
        },
        {
            size: 'Parking',
            title: 'Fleet Storage',
            description: 'Secure parking for work trucks, trailers, and vans.'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <section className="bg-green-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <SEO
                        title="Contractor & Business Storage in Lewisville, TX | Interskate Self Storage"
                        description="Drive-up business storage units for contractors, landscapers, e-commerce, and trades in Lewisville, TX. Gate access 6AM–10PM. Located on I-35E service road."
                        canonical="/business-storage-lewisville"
                    />
                    <h1 className="text-4xl md:text-5xl font-bold mb-4" data-testid="business-storage-title">
                        Contractor & Business Storage in Lewisville, TX
                    </h1>
                    <p className="text-xl text-green-100 max-w-3xl mx-auto mb-6">
                        Drive-up units for inventory & tools. 6 AM – 10 PM Gate Access.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="https://interskateselfstorage.storageunitsoftware.com/pages/rent"
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                                if (window.gtag) {
                                    window.gtag('event', 'conversion_intent', {
                                        'event_category': 'lead',
                                        'event_label': 'business_hero_rates',
                                        'facility_name': 'Interskate Self Storage'
                                    });
                                }
                            }}
                            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-green-600 bg-white rounded-lg hover:bg-gray-100 transition-colors shadow-lg"
                        >
                            See Business Rates
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </a>
                        <a
                            href="tel:4692516255"
                            onClick={() => {
                                if (window.gtag) {
                                    window.gtag('event', 'conversion_intent', {
                                        'event_category': 'lead',
                                        'event_label': 'business_hero_call',
                                        'facility_name': 'Interskate Self Storage'
                                    });
                                }
                            }}
                            className="inline-flex items-center px-8 py-3 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-green-600 transition-colors"
                        >
                            Call for Long-Term Deals
                        </a>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Time is money.</h2>
                        <p className="text-xl text-gray-700 leading-relaxed mb-6">
                            At <strong>Interskate Self Storage</strong>, we understand that local business owners don't have time to wait for elevators or navigate narrow hallways.
                        </p>
                        <p className="text-lg text-gray-700 leading-relaxed">
                            We offer exclusively <strong>drive-up, ground-level storage units</strong> designed for efficiency. Whether you are storing landscaping equipment, plumbing supplies, or e-commerce inventory, you can pull your work truck directly up to the door, load up, and get to the job site fast.
                        </p>
                    </div>
                </div>
            </section>

            {/* Why Businesses Choose Us */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Businesses Choose Us
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                        {benefits.map((benefit, index) => (
                            <div key={index} className="bg-white p-6 rounded-lg shadow-sm flex items-start">
                                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mr-4 mt-1" />
                                <span className="text-lg text-gray-700 font-medium">{benefit}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who We Serve */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Perfect for Local Trades & Inventory
                        </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {businessTypes.map((type, index) => (
                            <div key={index} className="bg-gray-50 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                                <div className="mb-4">{type.icon}</div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">{type.title}</h3>
                                <p className="text-gray-600">{type.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Size Recommendations */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                        Size Recommendations for Business
                    </h2>
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="hidden md:grid grid-cols-3 bg-green-600 text-white p-4 font-bold text-lg">
                            <div>Unit Size</div>
                            <div className="col-span-2">Ideal For</div>
                        </div>
                        {sizeRecommendations.map((item, index) => (
                            <div key={index} className={`grid grid-cols-1 md:grid-cols-3 p-6 border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                <div className="font-bold text-xl text-green-800 mb-2 md:mb-0">{item.size}</div>
                                <div className="col-span-2">
                                    <span className="font-bold text-gray-900 block mb-1">{item.title}</span>
                                    <span className="text-gray-600">{item.description}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">
                        FAQ for Business Owners
                    </h2>
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Q: Do you accept deliveries?</h3>
                            <p className="text-gray-600">A: Please contact our office. We can often assist with accepting shipments for business tenants so you don't have to wait around.</p>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Q: Is there electrical power in the units?</h3>
                            <p className="text-gray-600">A: Our standard units are for storage only and do not include electrical outlets. This ensures the safety of the facility for all tenants.</p>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Q: Are my tools safe?</h3>
                            <p className="text-gray-600">A: We take security seriously. Our facility is fenced, well-lit, and monitored by 24/7 video surveillance. Access is controlled via a secure electronic gate code.</p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BusinessStorage;
